/**
 * @file apps/web/src/pages/portal/PortalLoginPage.tsx
 * @description Client portal login page.
 * Real credentials go through supabase.auth.signInWithPassword(), then
 * GET /auth/me hydrates the portal user (see LoginPage.tsx for the same
 * pattern on the SI side). The dev quick-login list below (DEV_MODE only)
 * bypasses Supabase entirely via POST /auth/dev-login + /auth/dev-token.
 * Route: /portal/login
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, LogIn } from 'lucide-react';
import { usePortalAuth, DEV_PORTAL_USERS } from '../../lib/portalAuth.js';
import { authApi } from '../../lib/api.js';
import { supabase } from '../../lib/supabase.js';

const DEV_PASSWORD = 'password123';

export function PortalLoginPage() {
  const { setUser, isLoggedIn } = usePortalAuth();
  const navigate = useNavigate();

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    if (isLoggedIn) navigate('/portal/dashboard', { replace: true });
  }, [isLoggedIn, navigate]);

  if (isLoggedIn) return null;

  // Real credentials — Supabase is the source of truth; GET /auth/me
  // hydrates userType/projectId/role from it (see LoginPage.tsx).
  const doLogin = async (loginEmail: string, loginPassword: string) => {
    setLoading(true);
    setError('');
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: loginPassword,
      });
      if (authError || !data.session) {
        throw new Error(authError?.message ?? 'Invalid email or password.');
      }

      const accessToken = data.session.access_token;
      const me = await authApi.me({ Authorization: `Bearer ${accessToken}` });

      if (me.userType !== 'client') {
        setError('This email is not registered for the client portal.');
        return;
      }

      // DEV_PORTAL_USERS only covers the hardcoded local seed accounts —
      // a real client user has no clientName source yet (same gap flagged
      // in c1b40a4 for the dev login path; see LoginPage.tsx's doLogin).
      const portalProfile = DEV_PORTAL_USERS.find(
        (u) => u.email.toLowerCase() === loginEmail.trim().toLowerCase(),
      );

      setUser({
        userId:     me.id,
        tenantId:   me.tenantId,
        email:      me.email,
        fullName:   me.fullName ?? me.email,
        projectId:  me.projectId,
        role:       me.role,
        clientName: portalProfile?.clientName ?? '',
        token:      accessToken,
      });
      navigate('/portal/dashboard');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  // Dev quick-login (DEV_MODE only) — bypasses Supabase via the API's own
  // dev-only routes, same as LoginPage.tsx's doDevLogin.
  const doDevLogin = async (loginEmail: string) => {
    setLoading(true);
    setError('');
    try {
      const profile = await authApi.devLogin(loginEmail, DEV_PASSWORD);

      if (profile.role !== 'client_admin' && profile.role !== 'client_user') {
        setError('This email is not registered for the client portal.');
        return;
      }

      const portalProfile = DEV_PORTAL_USERS.find(
        (u) => u.email.toLowerCase() === loginEmail.trim().toLowerCase(),
      );
      if (!portalProfile) {
        setError('This email is not registered for the client portal.');
        return;
      }

      const { token } = await authApi.devToken({ userId: profile.userId, tenantId: profile.tenantId });
      setUser({ ...portalProfile, token });
      navigate('/portal/dashboard');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Dev login failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError('Email and password are required.');
      return;
    }
    void doLogin(email.trim(), password);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: 'var(--gray)' }}
    >
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex items-center gap-2 justify-center mb-8">
          <div
            className="w-8 h-8 rounded flex items-center justify-center text-white text-sm font-mono font-bold"
            style={{ backgroundColor: 'var(--blue)' }}
          >
            V
          </div>
          <div>
            <span
              className="font-display text-xl font-bold tracking-tight block"
              style={{ color: 'var(--ink)' }}
            >
              Veridox
            </span>
            <span
              className="text-[10px] font-mono"
              style={{ color: 'var(--blue)' }}
            >
              Client Portal
            </span>
          </div>
        </div>

        <div className="card p-6 space-y-5">
          <div>
            <h1 className="font-display text-lg font-semibold text-ink">Welcome back</h1>
            <p className="text-sm text-muted mt-0.5">
              Enter your credentials to access your project portal.
            </p>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm">
              <AlertCircle size={14} className="shrink-0" /> {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="label mb-1.5">Email address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourclient.com"
                className="input"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="label mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="input"
                autoComplete="current-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center"
            >
              <LogIn size={15} />
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          {/* Dev quick-login — stripped from production builds entirely: Vite
              replaces import.meta.env.DEV with a literal `false` at build
              time, so esbuild's dead-code elimination drops this whole
              branch (JSX and the DEV_PORTAL_USERS import together) rather
              than just hiding it, keeping real seed-user emails out of the
              production bundle. */}
          {import.meta.env.DEV && (
            <div className="border-t border-[rgba(8,17,31,0.06)] pt-3 space-y-2">
              <p className="text-[11px] font-mono text-muted/70">Client portal accounts:</p>
              {DEV_PORTAL_USERS.map((u) => (
                <button
                  key={u.userId}
                  onClick={() => { setEmail(u.email); setPassword(DEV_PASSWORD); void doDevLogin(u.email); }}
                  className={[
                    'w-full text-left p-2.5 rounded-lg border text-sm transition-colors',
                    email === u.email
                      ? 'border-blue bg-sky text-ink'
                      : 'border-[rgba(8,17,31,0.08)] hover:border-blue/30 hover:bg-sky/20 text-ink',
                  ].join(' ')}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{u.fullName}</span>
                    <span className="text-[10px] font-mono text-muted">{u.role}</span>
                  </div>
                  <p className="text-[11px] font-mono text-muted mt-0.5">{u.email}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        <p className="text-center text-[11px] font-mono text-muted mt-4">
          Powered by Veridox · For internal use only
        </p>
      </div>
    </div>
  );
}
